import { Button, Label, NativeSelect, NativeSelectOption, ScrollArea, Separator } from 'shared-ui';

import { DateField } from '@/components/shared/DateField';
import { PanelCloseButton, SlidingPanel } from '@/components/shared/SlidingPanel';
import { ToggleRow } from '@/components/shared/ToggleRow';

import { DATE_RANGE_OPTIONS, LEAK_VISIBILITY_OPTIONS, SHOW_OPTIONS } from '../utils/mapFilters';
import { DEFAULT_ENTITY_MAP_FILTER_STATE } from '../utils/entityMapFilters';
import type { EntityMapFilterState } from '../utils/entityMapFilters';
import { leakMarkerImageId, poiMarkerImageId } from '../utils/entityMarkerArt';
import { enabledLeakStatuses, enabledPoiStatuses } from '../utils/entityStatus';
import type { LeakEntityStatus, PoiEntityStatus } from '../utils/entityStatus';
import { StatusMarkerIcon } from './StatusMarkerIcon';

// The split-model Filters panel: the sole home of per-status control (the Layers panel
// carries only the two entity toggles). Two status sections — one per family, driven by
// the enabled statuses in the registries — plus the shared Show / date / Leak Visibility /
// Pipe Type dimensions, under the same draft-then-Apply model as before. The Leak
// Visibility dropdown (a recorded physical attribute of the leak) is deliberately distinct
// from the Layers panel's Leaks toggle (marker visibility).

// Each row's icon sits in the designer's 19.38×24px box.
const STATUS_ICON_CLASS = 'h-6 w-[19.38px] shrink-0 object-contain';

export interface EntityFiltersPanelProps {
    isOpen: boolean;
    draft: EntityMapFilterState;
    onDraftChange: (next: EntityMapFilterState) => void;
    onApply: () => void;
    onReset: () => void;
    onClose: () => void;
    /** True when the user has explicitly applied filters. Reset is available whenever this is true. */
    isActive: boolean;
}

function FilterSelectField({
    id,
    label,
    value,
    options,
    onChange,
}: {
    id: string;
    label: string;
    value: string;
    options: Array<{ value: string; label: string }>;
    onChange: (v: string) => void;
}) {
    return (
        <div className="space-y-1.5">
            <Label htmlFor={id} className="text-foreground text-sm leading-5 font-medium">
                {label}
            </Label>
            <NativeSelect
                id={id}
                value={value}
                onChange={(e) => {
                    onChange(e.target.value);
                }}
                className="panel-select text-muted-foreground w-full text-sm leading-5 font-normal tracking-normal"
            >
                {options.map((o) => (
                    <NativeSelectOption key={o.value} value={o.value}>
                        {o.label}
                    </NativeSelectOption>
                ))}
            </NativeSelect>
        </div>
    );
}

function EntityFiltersPanelContent({
    draft,
    onDraftChange,
    onApply,
    onReset,
    onClose,
    isActive,
}: Omit<EntityFiltersPanelProps, 'isOpen'>) {
    function setPoiStatus(status: PoiEntityStatus, enabled: boolean) {
        onDraftChange({ ...draft, poiStatuses: { ...draft.poiStatuses, [status]: enabled } });
    }

    function setLeakStatus(status: LeakEntityStatus, enabled: boolean) {
        onDraftChange({ ...draft, leakStatuses: { ...draft.leakStatuses, [status]: enabled } });
    }

    function setPipeType(key: keyof EntityMapFilterState['pipeTypes'], enabled: boolean) {
        onDraftChange({ ...draft, pipeTypes: { ...draft.pipeTypes, [key]: enabled } });
    }

    const canReset = isActive || JSON.stringify(draft) !== JSON.stringify(DEFAULT_ENTITY_MAP_FILTER_STATE);

    return (
        <>
            <div className="flex h-20 shrink-0 items-center justify-between px-6">
                <h2 className="text-lg leading-7 font-black tracking-normal uppercase md:text-2xl md:leading-8">
                    Filters
                </h2>
                <PanelCloseButton onClick={onClose} aria-label="Close filters panel" />
            </div>

            <Separator />

            <ScrollArea className="min-h-0 flex-1">
                <div className="space-y-6 px-6 py-4">
                    <FilterSelectField
                        id="filter-show"
                        label="Show"
                        value={draft.show}
                        options={SHOW_OPTIONS}
                        onChange={(v) => {
                            onDraftChange({ ...draft, show: v as EntityMapFilterState['show'] });
                        }}
                    />

                    <div className="space-y-3">
                        <FilterSelectField
                            id="filter-poi-from"
                            label="Point of Interest (POI) From"
                            value={draft.dateRange}
                            options={DATE_RANGE_OPTIONS}
                            onChange={(v) => {
                                onDraftChange({ ...draft, dateRange: v as EntityMapFilterState['dateRange'] });
                            }}
                        />
                        {draft.dateRange === 'custom' && (
                            <div className="space-y-2">
                                <div className="grid grid-cols-2 gap-3">
                                    <DateField
                                        id="filter-date-from"
                                        label="From"
                                        placeholder="Choose Date"
                                        value={draft.customDateRange.from}
                                        onChange={(v) => {
                                            onDraftChange({
                                                ...draft,
                                                customDateRange: { ...draft.customDateRange, from: v },
                                            });
                                        }}
                                    />
                                    <DateField
                                        id="filter-date-to"
                                        label="To"
                                        placeholder="Choose Date"
                                        value={draft.customDateRange.to}
                                        onChange={(v) => {
                                            onDraftChange({
                                                ...draft,
                                                customDateRange: { ...draft.customDateRange, to: v },
                                            });
                                        }}
                                    />
                                </div>
                                {draft.customDateRange.from &&
                                    draft.customDateRange.to &&
                                    draft.customDateRange.from > draft.customDateRange.to && (
                                        <p className="text-destructive text-xs">From date must be before To date.</p>
                                    )}
                            </div>
                        )}
                    </div>

                    <FilterSelectField
                        id="filter-leak-visibility"
                        label="Leak Visibility"
                        value={draft.leakVisibility}
                        options={LEAK_VISIBILITY_OPTIONS}
                        onChange={(v) => {
                            onDraftChange({ ...draft, leakVisibility: v as EntityMapFilterState['leakVisibility'] });
                        }}
                    />

                    <div>
                        <p className="text-foreground mb-2 text-sm leading-5 font-medium">
                            Point of Interest (POI) Status
                        </p>
                        {enabledPoiStatuses().map((def) => (
                            <ToggleRow
                                key={def.status}
                                label={def.label}
                                icon={
                                    <StatusMarkerIcon
                                        imageId={poiMarkerImageId(def.status)}
                                        className={STATUS_ICON_CLASS}
                                    />
                                }
                                checked={draft.poiStatuses[def.status]}
                                onCheckedChange={(v) => {
                                    setPoiStatus(def.status, v);
                                }}
                            />
                        ))}
                    </div>

                    <div>
                        <p className="text-foreground mb-2 text-sm leading-5 font-medium">Leak Status</p>
                        {enabledLeakStatuses().map((def) => (
                            <ToggleRow
                                key={def.status}
                                label={def.label}
                                icon={
                                    <StatusMarkerIcon
                                        imageId={leakMarkerImageId(def.status)}
                                        className={STATUS_ICON_CLASS}
                                    />
                                }
                                checked={draft.leakStatuses[def.status]}
                                onCheckedChange={(v) => {
                                    setLeakStatus(def.status, v);
                                }}
                            />
                        ))}
                    </div>

                    <div>
                        <p className="text-foreground mb-2 text-sm leading-5 font-medium">Pipe Type</p>
                        <ToggleRow
                            label="Main"
                            checked={draft.pipeTypes.main}
                            onCheckedChange={(v) => {
                                setPipeType('main', v);
                            }}
                        />
                        <ToggleRow
                            label="Communication"
                            checked={draft.pipeTypes.communication}
                            onCheckedChange={(v) => {
                                setPipeType('communication', v);
                            }}
                        />
                        <ToggleRow
                            label="Customer Supply Line"
                            checked={draft.pipeTypes.customerSupplyLine}
                            onCheckedChange={(v) => {
                                setPipeType('customerSupplyLine', v);
                            }}
                        />
                        <ToggleRow
                            label="Stop Tap / Boundary Box"
                            checked={draft.pipeTypes.stopTapBoundaryBox}
                            onCheckedChange={(v) => {
                                setPipeType('stopTapBoundaryBox', v);
                            }}
                        />
                        <ToggleRow
                            label="Fitting"
                            checked={draft.pipeTypes.fitting}
                            onCheckedChange={(v) => {
                                setPipeType('fitting', v);
                            }}
                        />
                        <ToggleRow
                            label="Unknown"
                            checked={draft.pipeTypes.unknown}
                            onCheckedChange={(v) => {
                                setPipeType('unknown', v);
                            }}
                        />
                    </div>
                </div>
            </ScrollArea>

            <div className="shrink-0 space-y-2 border-t px-6 py-4">
                <Button className="h-10 w-full cursor-pointer" type="button" onClick={onApply}>
                    Apply
                </Button>
                <Button
                    variant="ghost"
                    type="button"
                    className="panel-close-btn text-primary h-10 w-full cursor-pointer"
                    onClick={onReset}
                    disabled={!canReset}
                >
                    Reset
                </Button>
            </div>
        </>
    );
}

export function EntityFiltersPanel(props: EntityFiltersPanelProps) {
    return (
        <SlidingPanel isOpen={props.isOpen} onClose={props.onClose} ariaLabel="Map filters" title="Filters">
            <EntityFiltersPanelContent {...props} />
        </SlidingPanel>
    );
}
